/**
 * OrderQuickView - Slide-over drawer showing order details
 * 
 * Features:
 * - Quick summary of order info
 * - Customer details
 * - Line items preview
 * - Status/payment info
 * - Quick action buttons
 * - Link to full detail page
 */
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { adminAPI } from '../../utils/api';
import { formatCurrency, formatDate, StatusCell } from '../../components/admin/AdminDataTable';
import {
  X,
  ExternalLink,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  CreditCard,
  FileText,
  Calendar,
  Hash,
  ChevronRight,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

// Status configs
const ORDER_STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  waiting_artwork: { label: 'Waiting Artwork', color: 'bg-yellow-100 text-yellow-700' },
  proof_sent: { label: 'Proof Sent', color: 'bg-indigo-100 text-indigo-700' },
  approved: { label: 'Approved', color: 'bg-teal-100 text-teal-700' },
  in_production: { label: 'In Production', color: 'bg-purple-100 text-purple-700' },
  processing: { label: 'Processing', color: 'bg-cyan-100 text-cyan-700' },
  ready: { label: 'Ready', color: 'bg-emerald-100 text-emerald-700' },
  shipped: { label: 'Shipped', color: 'bg-blue-100 text-blue-700' },
  closed: { label: 'Closed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const PAYMENT_STATUS_CONFIG = {
  unpaid: { label: 'Unpaid', color: 'bg-orange-100 text-orange-700' },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
};

const InfoRow = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`flex items-start gap-3 ${className}`}>
    <Icon className="w-4 h-4 text-ivory-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-ivory-500">{label}</p>
      <p className="text-sm text-ivory-800 truncate">{value || '—'}</p>
    </div>
  </div>
);

const OrderQuickView = ({
  open,
  onOpenChange,
  orderId,
  onViewFullDetails,
  onNavigateToCustomer,
  onNavigateToInvoice,
}) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch order details when opened
  useEffect(() => {
    if (!open || !orderId) {
      setOrder(null);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminAPI.getOrder(orderId);
        setOrder(data);
      } catch (err) {
        console.error('Failed to fetch order:', err);
        setError(err.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [open, orderId]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[450px] sm:max-w-[450px] p-0" data-testid="order-quick-view">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-ivory-50">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="font-mono text-lg">
                {orderId || 'Order Details'}
              </SheetTitle>
              <SheetDescription className="text-xs">
                Quick view • Click &quot;View Full Details&quot; for more
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Content */}
        <ScrollArea className="h-[calc(100vh-180px)]">
          {loading ? (
            <div className="p-6 space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : error ? (
            <div className="p-6 flex flex-col items-center justify-center text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-red-600 font-medium">Failed to load order</p>
              <p className="text-sm text-ivory-500 mt-1">{error}</p>
              <Button variant="outline" size="sm" onClick={handleClose} className="mt-4">
                Close
              </Button>
            </div>
          ) : order ? (
            <div className="p-6 space-y-6">
              {/* Status badges */}
              <div className="flex items-center gap-2">
                <StatusCell value={order.status} statusConfig={ORDER_STATUS_CONFIG} />
                <StatusCell value={order.payment_status} statusConfig={PAYMENT_STATUS_CONFIG} />
              </div>

              {/* Totals Summary */}
              <div className="bg-ivory-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-ivory-500">Subtotal</p>
                    <p className="text-lg font-semibold">{formatCurrency(order.subtotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ivory-500">Total</p>
                    <p className="text-xl font-bold text-gold-600">{formatCurrency(order.total)}</p>
                  </div>
                  {order.tax_amount > 0 && (
                    <div>
                      <p className="text-xs text-ivory-500">Tax</p>
                      <p className="text-sm">{formatCurrency(order.tax_amount)}</p>
                    </div>
                  )}
                  {order.shipping_amount > 0 && (
                    <div>
                      <p className="text-xs text-ivory-500">Shipping</p>
                      <p className="text-sm">{formatCurrency(order.shipping_amount)}</p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Customer Info */}
              <div>
                <h4 className="text-sm font-semibold text-ivory-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer
                </h4>
                <div className="space-y-3">
                  <InfoRow
                    icon={User}
                    label="Name"
                    value={order.customer_name}
                  />
                  <InfoRow
                    icon={Mail}
                    label="Email"
                    value={order.customer_email}
                  />
                  {order.customer_phone && (
                    <InfoRow
                      icon={Phone}
                      label="Phone"
                      value={order.customer_phone}
                    />
                  )}
                  {order.account_id && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => onNavigateToCustomer?.(order.account_id)}
                      className="text-blue-600 p-0 h-auto"
                    >
                      View Customer Profile
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>

              <Separator />

              {/* Order Details */}
              <div>
                <h4 className="text-sm font-semibold text-ivory-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Order Info
                </h4>
                <div className="space-y-3">
                  <InfoRow
                    icon={Hash}
                    label="Order ID"
                    value={order.order_id}
                  />
                  <InfoRow
                    icon={Calendar}
                    label="Created"
                    value={formatDate(order.created_at)}
                  />
                  {order.quote_id && (
                    <InfoRow
                      icon={FileText}
                      label="From Quote"
                      value={order.quote_id}
                    />
                  )}
                  {order.po_number && (
                    <InfoRow
                      icon={FileText}
                      label="PO Number"
                      value={order.po_number}
                    />
                  )}
                </div>
              </div>

              <Separator />

              {/* Line Items Preview */}
              <div>
                <h4 className="text-sm font-semibold text-ivory-700 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Items ({order.items?.length || 0})
                </h4>
                {order.items && order.items.length > 0 ? (
                  <div className="space-y-2">
                    {order.items.slice(0, 5).map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-ivory-50 rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ivory-800 truncate">
                            {item.product_name || item.sku || `Item ${idx + 1}`}
                          </p>
                          <p className="text-xs text-ivory-500">
                            Qty: {item.quantity} × {formatCurrency(item.unit_price)}
                          </p>
                        </div>
                        <p className="text-sm font-semibold ml-3">
                          {formatCurrency(item.line_total || item.quantity * item.unit_price)}
                        </p>
                      </div>
                    ))}
                    {order.items.length > 5 && (
                      <p className="text-xs text-ivory-500 text-center py-2">
                        +{order.items.length - 5} more items
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-ivory-500">No items</p>
                )}
              </div>

              {/* Shipping Address */}
              {order.shipping_address && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-ivory-700 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Shipping Address
                    </h4>
                    <div className="text-sm text-ivory-600 bg-ivory-50 rounded-md p-3">
                      {order.shipping_address.street && <p>{order.shipping_address.street}</p>}
                      {order.shipping_address.street2 && <p>{order.shipping_address.street2}</p>}
                      <p>
                        {[
                          order.shipping_address.city,
                          order.shipping_address.state,
                          order.shipping_address.zip
                        ].filter(Boolean).join(', ')}
                      </p>
                      {order.shipping_address.country && (
                        <p>{order.shipping_address.country}</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Admin Notes */}
              {order.admin_notes && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-ivory-700 mb-2">Admin Notes</h4>
                    <p className="text-sm text-ivory-600 bg-yellow-50 border border-yellow-100 rounded-md p-3">
                      {order.admin_notes}
                    </p>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </ScrollArea>

        {/* Footer Actions */}
        {!loading && !error && order && (
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t bg-white">
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleClose}
              >
                Close
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  handleClose();
                  onViewFullDetails?.(orderId);
                }}
                data-testid="view-full-details-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Full Details
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OrderQuickView;
